import { useAnalytics } from '@psyomics/ui-common';
import { useEffect } from 'react';
import { Model } from 'survey-react-ui';

import { useUpdateSurveyResponse } from '../../survey-responses/hooks/use-update-survey-response';

import { createSurveyEventHandlers } from './survey-event-handlers';
import { useSurveyUpdateQueue } from './use-survey-update-queue';

type SurveyData = Record<string, unknown>;

type UseSurveyHandlerProps = {
  invitePublicId: string;
  organisationCode?: string | null;
  ready: boolean;
  serviceCode?: string | null;
  setSurveyResults: (data: SurveyData) => void;
  surveyModel: Model;
  surveyResponseId: string;
  surveyResponseUpdatedAt?: Date;
  updateSurveyAnswers: ReturnType<typeof useUpdateSurveyResponse>[1];
};

const useSurveyHandlers = ({
  invitePublicId,
  organisationCode,
  ready,
  serviceCode,
  setSurveyResults,
  surveyModel,
  surveyResponseId,
  surveyResponseUpdatedAt,
  updateSurveyAnswers,
}: UseSurveyHandlerProps) => {
  const analytics = useAnalytics();

  const enqueueUpdateSurvey = useSurveyUpdateQueue({
    ready,
    setSurveyResults,
    surveyResponseId,
    surveyResponseUpdatedAt,
    updateSurveyAnswers,
  });

  useEffect(() => {
    const handlers = createSurveyEventHandlers({
      analytics,
      invitePublicId,
      organisationCode,
      serviceCode,
      updateSurvey: enqueueUpdateSurvey,
    });
    surveyModel.onCurrentPageChanged.add(handlers.onPageChange);
    surveyModel.onComplete.add(handlers.onComplete);
    surveyModel.onValueChanged.add(handlers.onValueChange);
    return () => {
      surveyModel.onValueChanged.clear();
      surveyModel.onCurrentPageChanged.remove(handlers.onPageChange);
      surveyModel.onComplete.clear();
    };
  }, [analytics, enqueueUpdateSurvey, invitePublicId, organisationCode, serviceCode, surveyModel]);
};

export { useSurveyHandlers };
